import React from "react";
import OpenLinkInNewTab from "shared/form/core/link";
import { useCopies } from "shared/hooks/useCopies";
import ToolTip from "shared/components/tooltip";
import Size from "shared/enums/Size";
import { getFormattedCurrency } from "shared/utils";
import useTicketPageService from "./service";
import classNames from "classnames";
import { TicketApiResponseType } from "apps/registration/types/regFormApiResponse";
import { PriceDetailsProp, TicketMessageProps, TicketType } from "./type";
import { isBuyerAttendee } from "apps/registration/utils";
import NoTicketsAvailable from "./noTicketsAvailable";
import sortBy from "lodash/sortBy";
import RegistrationWorkflowType from "shared/enums/RegistrationWorkflowType";
import QueryParamKey from "shared/enums/QueryParamKey";
import { RegistrationFormType } from "shared/enums/RegistrationFormType";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import ParticipantSubRole from "shared/enums/ParticipantSubRole";
import Button from "shared/components/button";
import Variant from "shared/enums/Variant";
import { Base64Util } from "shared/utils/base64";
// import Variant from "shared/enums/Variant";

// todo:sk check button variant css
// discuss with RR

/**
 * @description Component to list out tickets based on type(hosted, application, payment)
 * @param tickets Array of tickets to be shown
 *
 */

export default function TicketList(props: { ticketList: Array<TicketApiResponseType> }) {
  const service = useTicketPageService(props.ticketList);

  if (props.ticketList.length === 0) {
    return <NoTicketsAvailable />;
  }

  return (
    <section className="ticketList">
      {service.hostedTickets.length > 0 && (
        <ul>
          {sortBy(service.hostedTickets, "ticketPageDisplayOrder").map(ticket => (
            <Ticket key={ticket.id} ticket={ticket} getTicketUrl={service.getTicketUrl} />
          ))}
        </ul>
      )}
      {service.applicationOrPaymentTickets.length > 0 && (
        <ul>
          {sortBy(service.applicationOrPaymentTickets, "ticketPageDisplayOrder").map(ticket => (
            <Ticket key={ticket.id} ticket={ticket} getTicketUrl={service.getTicketUrl} />
          ))}
        </ul>
      )}
      {service.nonAttendeeTickets.length > 0 && (
        <ul>
          {sortBy(service.nonAttendeeTickets, "ticketPageDisplayOrder").map(ticket => (
            <Ticket key={ticket.id} ticket={ticket} getTicketUrl={service.getTicketUrl} />
          ))}
        </ul>
      )}
    </section>
  );
}
interface TicketNameProps {
  title: string;
  ticketDisplayColour: string;
  termsAndConditions?: string;
}
function TicketName(props: TicketNameProps) {
  return (
    <div className="ticket_name">
      <strong style={{ color: props.ticketDisplayColour }}>
        {props.title}
        {props.termsAndConditions && <ToolTip size={Size.SMALL} content={props.termsAndConditions} isHTMLFormatted />}
      </strong>
    </div>
  );
}

function TicketContainer(props: { children: React.ReactNode }) {
  return <li className="ticket">{props.children}</li>;
}

function TicketFooter(props: { children: React.ReactNode }) {
  return <div className="ticket_footer">{props.children}</div>;
}

interface TicketBodyProps {
  applicationFlow: boolean;
  paymentFlow: boolean;
  subRole: ParticipantSubRole;
  applicableRate: any;
  ticketDisplayColour: string;
  ticketsPageBody?: string;
}

function TicketBody(props: TicketBodyProps) {
  const { buyerAttendee } = React.useMemo(() => {
    return {
      buyerAttendee: isBuyerAttendee(props.subRole)
    };
  }, [props]);
  return (
    <div
      className={classNames("ticket_body", {
        "ticket_body--applicationFlow": props.applicationFlow
      })}>
      {(props.paymentFlow || buyerAttendee) && (
        <PriceDetails price={props.applicableRate} ticketColor={props.ticketDisplayColour} />
      )}
      {props.ticketsPageBody && <TicketMessage message={props.ticketsPageBody} />}
    </div>
  );
}

export function Ticket(props: TicketType) {
  const copies = useCopies();
  const [viewBulkTicket, setViewBulkTicket] = React.useState(false);
  const { ticketUrl } = React.useMemo(() => {
    return {
      buyerAttendee: isBuyerAttendee(props.ticket.subRole),
      ticketUrl: props.getTicketUrl(props.ticket)
    };
  }, [props]);

  return (
    <TicketContainer>
      <div>
        <TicketName
          title={props.ticket.title}
          ticketDisplayColour={props.ticket.ticketDisplayColour}
          termsAndConditions={props.ticket.termsAndConditions}
        />
        <TicketBody
          applicationFlow={props.ticket.applicationFlow}
          paymentFlow={props.ticket.paymentFlow}
          subRole={props.ticket.subRole}
          applicableRate={props.ticket.applicableRate}
          ticketDisplayColour={props.ticket.ticketDisplayColour}
          ticketsPageBody={props.ticket.ticketsPageBody}
        />

        <TicketFooter>
          <div
            className="ticketFooterActions"
            style={
              {
                "--primaryBtnBg": props.ticket.ticketDisplayColour,
                "--primary": props.ticket.ticketDisplayColour
              } as React.CSSProperties
            }>
            <OpenLinkInNewTab
              asButton
              url={ticketUrl}
              title={props.ticket.name}
              label={
                props.ticket.paymentFlow ||
                props.ticket.workflowType === RegistrationWorkflowType.NON_ATTENDEE_NO_APPROVAL
                  ? copies.get("get.ticket")
                  : copies.get("apply.now")
              }
              // variant={props.ticket.ticketDisplayColour ? Variant.NEUTRAL : undefined}
            />
            {props.ticket.bulkPurchaseAllowed && (
              <Button
                variant={Variant.PRIMARY}
                label={copies.get("bulk.ticket.buy.in.bulk")}
                isOutline
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setViewBulkTicket(true);
                }}
              />
            )}
          </div>
          {viewBulkTicket && (
            <Modal size={Size.EXTRA_SMALL} className="smallestModalContainer">
              <ModalHeader left={copies.get("bulk.ticket.buy.in.bulk")} onClose={() => setViewBulkTicket(false)} />
              <ModalBody>
                <BulkTicket {...props} />
              </ModalBody>
            </Modal>
          )}
        </TicketFooter>
      </div>
    </TicketContainer>
  );
}

export function BulkTicket(props: TicketType) {
  const copies = useCopies();
  const { bulkTicketUrl } = React.useMemo(() => {
    return {
      bulkTicketUrl:
        props.getTicketUrl(props.ticket) +
        `&${QueryParamKey.REGISTRATION_FORM_TYPE}=${Base64Util.encode(RegistrationFormType.BULK)}`
    };
  }, [props]);

  return (
    <TicketContainer>
      <div>
        <TicketName
          ticketDisplayColour={props.ticket.ticketDisplayColour}
          title={props.ticket.bulkTitle}
          termsAndConditions={props.ticket.bulkTermsAndConditions}
        />
        <div className="ticket_body">
          {props.ticket.bulkTicketsPageBody ? (
            <div dangerouslySetInnerHTML={{ __html: props.ticket.bulkTicketsPageBody }} />
          ) : (
            <p>{copies.get("tickets.page.bulk.ticket.page.body.empty")}</p>
          )}
        </div>

        <TicketFooter>
          <div
            className="ticketFooterActions"
            style={
              {
                "--primaryBtnBg": props.ticket.ticketDisplayColour
              } as React.CSSProperties
            }>
            <OpenLinkInNewTab
              backgroundColor={props.ticket.ticketDisplayColour}
              asButton
              url={bulkTicketUrl}
              title={props.ticket.name}
              label={copies.get("proceed")}
            />
          </div>
        </TicketFooter>
      </div>
    </TicketContainer>
  );
}

function PriceDetails(props: PriceDetailsProp) {
  const copies = useCopies();
  const { isFreeTicket, isDiscounted } = React.useMemo(() => {
    return {
      isFreeTicket: !props.price || props.price?.applicablePrice.value === 0,
      isDiscounted: props.price?.applicablePrice.value < props.price?.basePrice.value
    };
  }, [props.price]);

  return (
    <div className="ticket_body_payment">
      <p className="payment_registerText">{copies.get("ticket.register.now")}</p>
      {isFreeTicket ? (
        <strong className="payment_free" style={{ color: props.ticketColor }}>
          {copies.get("free").toUpperCase()}
        </strong>
      ) : (
        <p className="payment_rate" style={{ color: props.ticketColor }}>
          <strong>{getFormattedCurrency(props.price?.applicablePrice)}</strong>
        </p>
      )}
      {isDiscounted && !isFreeTicket && (
        <React.Fragment>
          <p
            className="payment_discount"
            dangerouslySetInnerHTML={{
              __html: copies.get("ticket.discount.text", {
                0: props.price?.discountPercentage
              })
            }}></p>
          <strong className="payment_base">{getFormattedCurrency(props.price?.basePrice)}</strong>
        </React.Fragment>
      )}
    </div>
  );
}

function TicketMessage(props: TicketMessageProps) {
  return (
    <p
      className="ticket_body_message"
      dangerouslySetInnerHTML={{
        __html: props.message
      }}></p>
  );
}
// refactor-rahil
